import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { Col, Row, Space, Divider } from 'antd'
import { formatAddress, formatDateTime, getGrnStatus, numberWithComma, roundNumber } from 'helpers/format'
import { GrnDetailReq, UpdateGrnReq } from 'Store/grn/types'
import { getGrnDetailAPI, updateGrnAPI } from 'Store/grn/api'
import { selectGrnActionEvent, selectGrnDetail, selectGrnDetailEvent } from 'Store/grn/selector'
import { selectCurrentUser } from 'Store/user/selector'
import EventModal from 'Components/modal/EventModal'
import { clearEventAction } from 'Store/grn/actions'
import { clearEventAction as clearPaymentEventAction } from 'Store/grn-payment/actions'
import LoadingView from 'Components/modal/LoadingView'
import { DetailDrawer } from 'Components/common/MainLayout'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { selectAllSupplierList } from 'Store/supplier/selector'
import { InfoItem } from 'Pages/Merchants/components'
import { ExpireDate, StatusTag } from 'Components/utilities'
import { PrimaryButton } from 'Components/button'
import ConfirmPayDept from './components/confirm_paydept'
import GrnItemProducts from './components/GrnItemProducts'
import { MultiUploadForm } from 'Components/form'
import { CreateCreditReq, CreatePaymentReq, CreditNoteItem, PatialPaymentReq } from 'Store/grn-payment/types'
import { createCredutNoteAPI, createPaymentAPI, patialPaymentAPI } from 'Store/grn-payment/api'
import { selectCreditNoteActionEvent, selectPaymentActionEvent } from 'Store/grn-payment/selector'
import { GrnStatus } from 'helpers/constanst'
import { getUserLogin } from 'helpers/account'
import AmountPayDept from './components/AmountPaydept'
import CnListTable from './components/CnListTable'
import PVListTable from './components/PVListTable'
import ReduceDept from './components/ReduceDept'

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  onSuccess?: () => void
  onPaymentSuccess?: () => void
  id?: number
  viewMode?: boolean
}

const GrnDetail: React.FunctionComponent<Props> = props => {
  const {
    onCloseDrawer,
    visible,
    currentUser,
    event,
    onSuccess = () => {},
    id = 0,
    detail,
    detailEvent,
    paymentEvent,
    onPaymentSuccess = () => {},
    creditNoteAction,
    viewMode = false,
  } = props
  const [showConfirm, setShowConfirm] = useState(false)
  const [showAmount, setShowAmount] = useState(false)
  const [showReduceForm, setShowReduceForm] = useState(false)
  const [files, setFiles] = useState([] as string[])
  const isPending = detail?.status === GrnStatus.PENDING
  const isPatial = detail?.status === GrnStatus.PATIAL
  const userInfo = getUserLogin()

  const dispatch = useDispatch()

  useEffect(() => {
    if (visible) {
      onGetDetail()
      setFiles([])
    }
  }, [visible])

  useEffect(() => {
    if (detail?.delivery_note) {
      setFiles(detail?.delivery_note || [])
    }
  }, [detail])

  const onGetDetail = (): void => {
    const req: GrnDetailReq = {
      id,
    }
    dispatch(getGrnDetailAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onFullPayment = (): void => {
    const req: CreatePaymentReq = {
      supplier_id: detail?.supplier?.id || 0,
      grns: detail ? [detail.id || 0] : [],
      username: userInfo?.username || '',
    }
    dispatch(createPaymentAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onPayment = (price: number): void => {
    if (detail?.status === GrnStatus.PATIAL || price !== detail?.total_recieve_price || 0) {
      onPatialPayment(price)
    } else {
      onFullPayment()
    }
  }

  const onPatialPayment = (price: number): void => {
    const req: PatialPaymentReq = {
      supplier_id: detail?.supplier?.id || 0,
      grn_id: detail?.id || 0,
      username: userInfo?.username || '',
      price: price,
    }
    dispatch(patialPaymentAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onCreateCreditNote = (item: CreditNoteItem[], remark: string): void => {
    const req: CreateCreditReq = {
      grn_id: detail?.id || 0,
      username: userInfo?.username || '',
      credit_note_item: item,
      remark: remark,
    }
    dispatch(createCredutNoteAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onUpdate = (): void => {
    const req: UpdateGrnReq = {
      id: detail?.id || 0,
      delivery_note: files,
    }
    dispatch(updateGrnAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const clearEvent = (): void => {
    dispatch(clearEventAction())
    dispatch(clearPaymentEventAction())
  }

  const onEventSuccess = (): void => {
    setShowConfirm(false)
    setShowReduceForm(false)
    clearEvent()
    onGetDetail()
  }

  const onPaymentEventSuccess = (): void => {
    setShowConfirm(false)
    setShowAmount(false)
    clearEvent()
    onGetDetail()
    onSuccess()
    onPaymentSuccess()
  }

  const renderInfo = (title: string, value: any, span: number = 15) => {
    return <InfoItem title={title} value={value} span={span} />
  }

  const renderGrnInfo = () => {
    return (
      <BoxWrapper>
        <Row gutter={8}>
          <Col md={12} xs={24}>
            <BoxTitle>รายละเอียดการรับเข้า</BoxTitle>
            {renderInfo('เลขที่ใบจ่ายหนี้', detail?.grn_no)}
            {renderInfo('เลขที่ใบส่งสินค้าจากผู้จำหน่าย', detail?.supplier_shipping_no)}
            {renderInfo('วันที่รับเข้าคลัง', formatDateTime(detail?.recieved_date || ''))}
            {(isPending || isPatial) &&
              renderInfo(
                'วันที่ครบกำหนดชำระ',
                <ExpireDate date={detail?.payment_date || ''} direction="horizontal" />,
              )}
            {renderInfo('ผู้รับเข้า', detail?.username)}
            {renderInfo('สถานะ', renderStatus(detail?.status || ''))}
          </Col>
          <Col md={12} xs={24}>
            <BoxTitle>รายละเอียดการสั่งซื้อ</BoxTitle>
            <Row gutter={8}>
              <Col md={24} xs={24}>
                {renderInfo('เลขที่ใบสั่งซื้อ', detail?.purchasing?.purchasing_no)}
                {renderInfo('วันที่สั่งซื้อ', formatDateTime(detail?.purchasing?.purchasing_date || ''))}
                {renderInfo('ผู้ออกใบสั่งซื้อ', detail?.purchasing?.purchasing_by)}
                {renderInfo('หมายเหตุ', detail?.purchasing?.remark)}
              </Col>
            </Row>
          </Col>
        </Row>
      </BoxWrapper>
    )
  }

  const countCreditNoteTotal = () => {
    let total = 0
    detail?.credit_notes &&
      detail.credit_notes.forEach(item => {
        total += item.total_price || 0
      })
    return total
  }

  const countPaymentTotal = () => {
    let total = 0
    detail?.payments &&
      detail.payments.forEach(item => {
        total += item.grn_item_price || item.total_price
      })
    return total
  }

  const renderSummaryInfo = () => {
    return (
      <BoxWrapper>
        <BoxTitle>สรุป</BoxTitle>
        <Row gutter={8}>
          <Col span={24}>
            {renderInfo(
              'จำนวนเงินรวมทั้งหมด',
              `${numberWithComma(detail?.total_original_recieve_price || 0, true)} บาท`,
            )}
            {renderInfo('ยอดลดหนี้ทั้งหมด', <span>{numberWithComma(countCreditNoteTotal(), true)} บาท</span>)}
            {renderInfo('ยอดชำระทั้งหมด', <span>{numberWithComma(countPaymentTotal(), true)} บาท</span>)}
            <Divider />
            {renderInfo(
              'ยอดที่ต้องชำระ',
              <Price>{numberWithComma(roundNumber(detail?.total_recieve_price || 0), true)} บาท</Price>,
            )}
          </Col>
        </Row>
      </BoxWrapper>
    )
  }

  const renderPurchaseInfo = () => {
    return (
      <BoxWrapper>
        <BoxTitle>รายละเอียดผู้จำหน่าย</BoxTitle>
        <Row gutter={8}>
          <Col md={24} xs={24}>
            {renderInfo('บริษัท/Supplier', detail?.supplier?.name)}
            {renderInfo('ที่อยู่', detail?.supplier?.address)}
            {renderInfo('เบอร์โทรศัพท์', detail?.supplier?.mobile_no)}
          </Col>
        </Row>
      </BoxWrapper>
    )
  }

  const renderClientInfo = () => {
    return (
      <BoxWrapper>
        <BoxTitle>รายละเอียดร้านค้า</BoxTitle>
        {renderInfo('ชื่อ', detail?.merchant?.name)}
        {renderInfo(
          'ที่อยู่',
          formatAddress(
            detail?.merchant?.address,
            detail?.merchant?.district,
            detail?.merchant?.amphor,
            detail?.merchant?.province,
            detail?.merchant?.postcode,
          ),
        )}
        {renderInfo('เบอร์โทรศัพท์', detail?.merchant?.phone)}
      </BoxWrapper>
    )
  }

  const renderPurchaseForm = () => {
    return (
      <BoxWrapper>
        <Row gutter={8}>
          <Col md={24} xs={24}>
            <MultiUploadForm
              label="Upload ใบส่งของ/ใบรับของ"
              token={currentUser.accessToken}
              onUpdateData={files => setFiles(files)}
              files={files}
              folder="delivery_note"
              listType="picture"
              name="delivery-note"
              description="สามารถอัพโหลดได้มากกว่า 1 รายการ (.png .jpg .pdf)"
              isRequired={false}
              isError={false}
              key="delivery_note"
              maxFileSize={5000}
              readOnly={viewMode}
            />
          </Col>
          {!viewMode && (
            <Col>
              <PrimaryButton btnType="Primary" text={`บันทึกข้อมูล`} onClick={onUpdate} />
            </Col>
          )}
        </Row>
      </BoxWrapper>
    )
  }

  const renderStatus = (status: string) => {
    const statusResult = getGrnStatus(status)
    return <StatusTag text={statusResult.text} color={statusResult.color} />
  }

  const renderFooterContent = () => {
    return (
      <Row justify="end" align="middle" gutter={[16, 8]}>
        <Col>
          <Space>
            {/* {isPatial && (
              <span>
                ชำระแล้ว{' '}
                <SuccessPrice>
                  {numberWithComma(
                    (detail?.total_original_recieve_price || 0) - (detail?.total_recieve_price || 0),
                    true,
                  )}
                </SuccessPrice>{' '}
                บาท
              </span>
            )} */}
            ยอดที่ต้องชำระ
            <Price>{numberWithComma(roundNumber(detail?.total_recieve_price || 0), true)}</Price> บาท
          </Space>
        </Col>
        <Col>
          <PrimaryButton btnType="Reject" text={`ลดหนี้`} onClick={() => setShowReduceForm(true)} />
        </Col>
        <Col>
          <PrimaryButton btnType="Approve" text={`ชำระหนี้`} onClick={() => setShowAmount(true)} />
        </Col>
      </Row>
    )
  }

  const ConfirmModal = (
    <ConfirmPayDept
      onCloseModal={() => setShowConfirm(false)}
      onConfirm={onFullPayment}
      visible={showConfirm}
      supplier={detail?.supplier}
      grnList={detail ? [detail] : []}
    />
  )

  const AmountModal = (
    <AmountPayDept
      onCloseModal={() => setShowAmount(false)}
      onConfirm={(amount: number) => onPayment(amount)}
      visible={showAmount}
      depy_amount={detail?.total_recieve_price || 0}
      grn={detail}
    />
  )

  const ReduceDeptModal = (
    <ReduceDept
      onCloseModal={() => setShowReduceForm(false)}
      onConfirm={onCreateCreditNote}
      visible={showReduceForm}
      products={detail?.items || []}
      depy_amount={detail?.total_recieve_price || 0}
    />
  )

  const paymentEventModel = (
    <EventModal
      event={[paymentEvent]}
      enable={true}
      successCalback={onPaymentEventSuccess}
      errorCalback={clearEvent}
    />
  )

  return (
    <DetailDrawer
      title={`เลขที่ใบจ่ายหนี้ : ${detail?.grn_no}`}
      onCloseDrawer={onCloseDrawer}
      visible={visible}
      width={'90%'}
      footer={(isPending || isPatial) && !viewMode && renderFooterContent()}
      maskClosable={true}
    >
      <LoadingView isShow={detailEvent.IsLoading} />
      <EventModal
        event={[event, creditNoteAction]}
        enable={true}
        successCalback={onEventSuccess}
        errorCalback={clearEvent}
      />
      {ConfirmModal}
      {paymentEventModel}
      {AmountModal}
      {ReduceDeptModal}
      <Row justify="space-between" gutter={16}>
        <Col md={24} xs={24}>
          {renderGrnInfo()}
        </Col>
        <Col md={12} xs={24}>
          {renderPurchaseInfo()}
        </Col>
        <Col md={12} xs={24}>
          {renderClientInfo()}
        </Col>
        <Col>
          <GrnItemProducts
            products={detail?.items || []}
            originalAmount={detail?.total_original_recieve_price || 0}
          />
        </Col>
        <Col span={24}>
          <Row gutter={[8, 8]}>
            <Col md={12} xs={24}>
              <Col span={24}>
                <CnListTable creditNotes={detail?.credit_notes || []} grnData={detail} />
              </Col>
              <Col span={24}>
                <PVListTable payments={detail?.payments || []} viewMode={viewMode} />
              </Col>
            </Col>
            <Col md={12} xs={24}>
              {renderSummaryInfo()}
              {renderPurchaseForm()}
            </Col>
          </Row>
        </Col>
        {/* <Col md={12} xs={24}>
          {renderPurchaseForm()}
        </Col> */}
      </Row>
    </DetailDrawer>
  )
}

const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
`

const BoxWrapper = styled.div`
  width: 100%;
  border-radius: 10px;
  padding: 15px 10px;
  //border: 1px solid ${(props: { theme: MyTheme }): string => props.theme.colors.whitebg};
  border: 1px solid #f0f0f0;
  margin-bottom: 10px;
  background-color:  ${(props: { theme: MyTheme }): string => props.theme.colors.white};
`

const Price = styled.div`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.title1};
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    event: selectGrnActionEvent(state),
    detail: selectGrnDetail(state),
    detailEvent: selectGrnDetailEvent(state),
    supplierList: selectAllSupplierList(state),
    paymentEvent: selectPaymentActionEvent(state),
    creditNoteAction: selectCreditNoteActionEvent(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(GrnDetail)
